import { cn } from "@win-reality/react-ui";

import ballDark from "@/assets/tagging/pitch-results/dark/ball.svg";
import calledStrikeDark from "@/assets/tagging/pitch-results/dark/called-strike.svg";
import catchersIntDark from "@/assets/tagging/pitch-results/dark/catchers-int.svg";
import foulDark from "@/assets/tagging/pitch-results/dark/foul.svg";
import hitByPitchDark from "@/assets/tagging/pitch-results/dark/hit-by-pitch.svg";
import inPlayDark from "@/assets/tagging/pitch-results/dark/in-play.svg";
import notAPitchDark from "@/assets/tagging/pitch-results/dark/not-a-pitch.svg";
import swingingStrikeDark from "@/assets/tagging/pitch-results/dark/swinging-strike.svg";
import ballWhite from "@/assets/tagging/pitch-results/white/ball.svg";
import calledStrikeWhite from "@/assets/tagging/pitch-results/white/called-strike.svg";
import catchersIntWhite from "@/assets/tagging/pitch-results/white/catchers-int.svg";
import foulWhite from "@/assets/tagging/pitch-results/white/foul.svg";
import hitByPitchWhite from "@/assets/tagging/pitch-results/white/hit-by-pitch.svg";
import inPlayWhite from "@/assets/tagging/pitch-results/white/in-play.svg";
import notAPitchWhite from "@/assets/tagging/pitch-results/white/not-a-pitch.svg";
import swingingStrikeWhite from "@/assets/tagging/pitch-results/white/swinging-strike.svg";

import { useTaggingTheme } from "../../hooks/useTaggingTheme";
import { HotkeyBadge } from "./OptionButton";

/** The "White" set reads on the light theme, the "Dark" set on the dark theme. */
const ICONS: Record<string, { dark: string; white: string }> = {
  Ball: { dark: ballDark, white: ballWhite },
  CalledStrike: { dark: calledStrikeDark, white: calledStrikeWhite },
  SwingingStrike: { dark: swingingStrikeDark, white: swingingStrikeWhite },
  Foul: { dark: foulDark, white: foulWhite },
  InPlay: { dark: inPlayDark, white: inPlayWhite },
  HitByPitch: { dark: hitByPitchDark, white: hitByPitchWhite },
  CatchersInt: { dark: catchersIntDark, white: catchersIntWhite },
  NotAPitch: { dark: notAPitchDark, white: notAPitchWhite },
};

type PitchResultTileProps = {
  value: string;
  label: string;
  hotkey?: string;
  isSelected?: boolean;
  /** The tracker suggested this option (for tests; not shown) */
  isSuggested?: boolean;
  onClick: () => void;
  /** Test hook: literal id from the call site */
  "data-testid"?: string;
};

export const PitchResultTile = ({
  value,
  label,
  hotkey,
  isSelected = false,
  isSuggested = false,
  onClick,
  "data-testid": testId,
}: PitchResultTileProps) => {
  const { theme } = useTaggingTheme();
  const icon = ICONS[value]?.[theme === "dark" ? "dark" : "white"];

  return (
    <button
      type="button"
      data-testid={testId}
      data-value={value}
      data-suggested={isSuggested || undefined}
      aria-pressed={isSelected}
      onClick={onClick}
      className={cn(
        // Size
        "h-20 w-full",
        // Layout
        "relative flex flex-col items-center justify-center",
        // Spacing
        "p-2",
        // Border
        "rounded-[5px] border",
        // Text
        "text-sm font-semibold uppercase text-gray-900 dark:text-white",
        // Effects
        "transition-colors",
        // States
        isSelected
          ? "border-tagging-option-selected-border bg-tagging-option-selected"
          : cn(
              "border-tagging-option-border bg-tagging-option hover:border-tagging-option-selected-border/50",
              // The dark option border matches the tile, which sits on a
              // card of nearly the same color: lighten it to show the edge
              "dark:border-white/15 dark:hover:border-tagging-option-selected-border/50",
            ),
      )}
    >
      {hotkey && (
        <HotkeyBadge hotkey={hotkey} className="absolute right-1.5 top-1.5" />
      )}
      {icon && <img src={icon} alt="" className="h-10 w-auto" />}
      <span className="text-center leading-tight">{label}</span>
    </button>
  );
};
